/**
 * StaticBuildupSkillEffect — passive that registers a static_charge instance
 * on the ChargeSystem when the player unlocks the Static node.
 *
 * Moving accumulates charge: walking trickles, running fills faster, dashing
 * dumps a flat burst. Charge lingers and bleeds off slowly after 5 seconds of
 * no movement. The next Lightning skill consumes the entire stockpile as bonus
 * damage via chargeBonusDamageRatio.
 *
 * This is the Fork 1 gate node on the Lightning trunk — unlocking it triggers
 * the fork UI for Arc / Pulse / Discharge.
 *
 * All values are data-driven from lightning.json. Downstream passives (Loaded)
 * modify rates through UpgradeEffectResolver tree-scope keys.
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { ChargeSystem, ChargeConfig } from '../../../systems/ChargeSystem.js';

// ---------------------------------------------------------------------------
// Dependencies
// ---------------------------------------------------------------------------

export interface StaticBuildupDeps {
  chargeSystem: ChargeSystem;
  resolver: {
    getTreePassiveEffect(tree: string, key: string, aggregation: string): number;
  };
}

// ---------------------------------------------------------------------------
// Config (populated from lightning.json effect block)
// ---------------------------------------------------------------------------

export interface StaticBuildupConfig {
  readonly walkChargeRate: number;
  readonly runChargeRate: number;
  readonly dashChargeRate: number;
  readonly decayDelayMs: number;
  readonly decayRate: number;
  readonly maxCharge: number;
  readonly chargeBonusDamageRatio: number;
}

// ---------------------------------------------------------------------------
// Defaults (mirror the JSON values — used when no config override is provided)
// ---------------------------------------------------------------------------

const DEFAULT_CONFIG: StaticBuildupConfig = {
  walkChargeRate: 1,
  runChargeRate: 2.5,
  dashChargeRate: 15,
  decayDelayMs: 5000,
  decayRate: 5,
  maxCharge: 100,
  chargeBonusDamageRatio: 0.1,
};

// ---------------------------------------------------------------------------
// Charge instance ID — shared constant so consuming skills use the same key
// ---------------------------------------------------------------------------

export const STATIC_CHARGE_ID = 'static_charge';

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class StaticBuildupSkillEffect implements SkillEffect {
  readonly skillId = 'static_buildup';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: StaticBuildupDeps;
  private readonly config: StaticBuildupConfig;

  constructor(deps: StaticBuildupDeps, config?: Partial<StaticBuildupConfig>) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.registerCharge();
  }

  /** Build the ChargeConfig from base values and register with ChargeSystem. */
  private registerCharge(): void {
    const chargeConfig: ChargeConfig = {
      id: STATIC_CHARGE_ID,
      maxCharge: this.config.maxCharge,
      decayDelayMs: this.config.decayDelayMs,
      decayRate: this.config.decayRate,
      walkChargeRate: this.config.walkChargeRate,
      runChargeRate: this.config.runChargeRate,
      dashChargeRate: this.config.dashChargeRate,
    };

    this.deps.chargeSystem.register(chargeConfig);
  }

  /**
   * Resolve the current chargeBonusDamageRatio including any tree-scope
   * modifiers from downstream passives.
   */
  getChargeBonusDamageRatio(): number {
    const additiveBonus = this.deps.resolver.getTreePassiveEffect(
      'lightning', 'chargeBonusDamageRatio', 'sum',
    );
    return this.config.chargeBonusDamageRatio + additiveBonus;
  }

  /** No per-frame work — ChargeSystem.update() is called by the movement system. */
  update(_delta: number): void {}

  destroy(): void {}
}
